// src/components/Aside.jsx
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import {
  ProSidebar,
  Menu,
  MenuItem,
  SubMenu,
  SidebarHeader,
  SidebarFooter,
  SidebarContent,
} from "react-pro-sidebar";

import {
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  Box,
} from "@mui/material";

import {
  AccountCircle,
  Close,
  Dashboard,
  School,
  MenuBook,
  Person,
  GroupAdd,
  BusinessCenter,
  Equalizer,
  Settings,
  HelpRounded,
  ChevronLeft,
  ChevronRight,
  AdminPanelSettings,
} from "@mui/icons-material";

import perfil from "../../assets/perfil.png";
import "../Scss/stylesComponents.scss?v=1.1.6";

import { PermissionContext, UserContext } from "../Context/Context";
import { usePermissions } from "../Context/PermissionProvider";
import ChangeMode from "./ChangeMode";

function Aside({
  image,
  collapsed = false,
  rtl,
  toggled = false,
  handleToggleSidebar,
  handleCollapseSidebar,
  userLogged,
  isMobile = false,
  theme,
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { logout, userData } = useContext(UserContext);
  const location = useLocation();
  const isReportsPage = location.pathname.startsWith("/informes");
  const { newRoleConfig, exitTestMode } = useContext(PermissionContext);
  const { can } = usePermissions();

  const [openDialog, setOpenDialog] = useState(false);
  const [openNewUserMode, setOpenNewUserMode] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const shortcuts = [
    { id: "create-contact", label: "Crear Contacto", path: "/contactos/crear" },
    {
      id: "create-expedient",
      label: "Crear Expediente",
      path: "/expedientes/crear",
    },
  ];

  const menuConfig = useMemo(
    () => [
      {
        type: "submenu",
        id: "perfil",
        label: "perfil",
        icon: <AccountCircle />,
        items: [
          { id: "perfil-datos", label: "datos", path: "/perfil" },
          { id: "perfil-mensajes", label: "mensajes", path: "/mensajes" },
          { id: "perfil-logout", label: "cerrarSesion", action: "logout" },
        ],
      },
      {
        type: "menu",
        id: "main-1",
        items: [
          {
            id: "main-panel",
            icon: <Dashboard />,
            label: "panel",
            path: "/panel",
          },
          {
            id: "main-contactos",
            icon: <Person />,
            label: "contactos",
            path: "/contactos",
            module: "contacts",
            action: "view",
          },
          {
            id: "main-clientes",
            icon: <GroupAdd />,
            label: "clientes",
            path: "/clientes",
            module: "clients",
            action: "view",
          },
          {
            id: "main-expedientes",
            icon: <BusinessCenter />,
            label: "expedientes",
            path: "/expedientes",
            module: "cases",
            action: "view",
          },
        ],
      },
      {
        type: "submenu",
        id: "config",
        label: "configuracion",
        icon: <Settings />,
        items: [
          {
            id: "cfg-empresas",
            path: "/empresas",
            label: "empresas",
            module: "company",
            action: "view",
          },
          {
            id: "cfg-asociaciones",
            path: "/asociaciones",
            label: "asociaciones",
            module: "association",
            action: "view",
          },
          // {
          //   id: "cfg-despachos",
          //   path: "/despachos",
          //   label: "despachos",
          //   module: "office",
          //   action: "view",
          // },
          {
            id: "cfg-grupos",
            path: "/grupos",
            label: "grupos",
            module: "group",
            action: "view",
          },
          {
            id: "cfg-usuarios",
            path: "/usuarios",
            label: "usuarios",
            module: "users",
            action: "view",
          },
          {
            id: "cfg-roles",
            path: "/roles",
            label: "tiposDeUsuarios",
            module: "userstypes",
            action: "view",
          },
          {
            id: "cfg-cases",
            path: "/expedientes-config",
            label: "expedientesConfig",
            module: "cases",
            action: "edit",
          },
          {
            id: "cfg-tipos-casos",
            path: "/tiposdecasos",
            label: "tiposCasos",
            module: "casestypes",
            action: "view",
          },
          {
            id: "cfg-contratos",
            path: "/contratos",
            label: "hojasDeCargo",
            module: "contracts",
            action: "view",
          },
        ],
      },
      {
        type: "submenu",
        id: "informes",
        label: "informes",
        icon: <Equalizer />,
        items: [
          {
            id: "informe-general",
            label: "informeGeneral",
            path: "/informes/informe-general",
            module: "reports",
            action: "view",
          },
        ],
      },
      {
        type: "menu",
        id: "main-2",
        items: [
          {
            id: "main-biblioteca",
            icon: <MenuBook />,
            label: "biblioteca",
            path: "/biblioteca",
          },
          {
            id: "main-manual",
            icon: <School />,
            path: "/manual",
            label: "manual",
            href: "https://crm.consulting-fidelity.com/uploads/manual-crm-legal.pdf",
            disabled: true,
          },
        ],
      },
    ],
    [],
  );

  const cerrarSesion = () => {
    logout(true);
  };

  const isItemAllowed = useCallback(
    (item) => {
      if (!item?.module || !item?.action) return true;
      return can(item.module, item.action);
    },
    [can],
  );

  const visibleMenuConfig = useMemo(
    () =>
      menuConfig
        .map((section) => {
          if (section.type !== "submenu") return section;

          const visibleItems = (section.items || []).filter(isItemAllowed);
          return {
            ...section,
            items: visibleItems,
          };
        })
        .filter(
          (section) =>
            section.type !== "submenu" ||
            (section.items && section.items.length),
        ),
    [menuConfig, isItemAllowed],
  );

  useEffect(() => {
    const activeSection = visibleMenuConfig.find((section) => {
      if (section.type !== "submenu") return false;

      return section.items.some((item) => {
        if (!item?.path) return false;

        return (
          location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`)
        );
      });
    });

    if (activeSection?.id) {
      setOpenSubMenu(activeSection.id);
    }
  }, [location.pathname, visibleMenuConfig]);

  const renderNavOrAnchor = (item) => {
    const label = intl.formatMessage({ id: item.label });

    if (item.disabled) {
      return <span className="menu-item-disabled">{label}</span>;
    }

    if (item.path) {
      return (
        <NavLink
          to={item.path}
          onClick={() => {
            setOpenSubMenu(null);
            if (isMobile) handleToggleSidebar(false);
          }}
          className={({ isActive }) =>
            isActive ? "menu-item-active" : undefined
          }
        >
          {label}
        </NavLink>
      );
    }

    if (item.href) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            setOpenSubMenu(null);
            if (isMobile) handleToggleSidebar(false);
          }}
        >
          {label}
        </a>
      );
    }

    if (item.action === "logout") {
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={cerrarSesion}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              cerrarSesion();
            }
          }}
          style={{ cursor: "pointer" }}
        >
          {label}
        </span>
      );
    }

    return <span>{label}</span>;
  };

  return (
    <div className={"sidebar-wrapper"} style={{}}>
      {!isMobile && (
        <div
          className={`sidebar-toggle-btn desktop-toggle ${collapsed ? "collapsed" : ""}`}
          onClick={() => handleCollapseSidebar()}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            left: collapsed ? 80 : 270,
          }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </div>
      )}

      <ProSidebar
        rtl={rtl}
        collapsed={false}
        toggled={toggled}
        breakPoint="md"
        className={`proSideBarClass ${isMobile ? "mobile-top" : ""} ${
          !isMobile && collapsed ? "collapsed" : ""
        } ${isReportsPage ? "reports-theme" : ""}`}
        style={{
          width: isMobile ? "100%" : collapsed ? "80px" : "",
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? 0 : "auto",
          left: 0,
          height: isMobile ? "100vh" : "100%",
          zIndex: isMobile ? 1400 : "auto",
          background: isReportsPage
            ? "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(2,6,23,0.98))"
            : undefined,
          transform: isMobile
            ? toggled
              ? "translateY(0)"
              : "translateY(-100%)"
            : "none",
          transition: "width 0.5s ease, transform 0.35s ease",
        }}
      >
        {isMobile && toggled && (
          <div
            onClick={() => handleToggleSidebar(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
            }}
          />
        )}

        <SidebarHeader>
          {isMobile && (
            <IconButton
              onClick={() => handleToggleSidebar(false)}
              sx={{
                position: "absolute",
                left: 12,
                top: 12,
                color: "white",
                zIndex: 2,
              }}
            >
              <Close />
            </IconButton>
          )}

          <div className="sidebarHeaderClass">
            {userLogged?.name ? (
              <div
                className={`userInfo ${collapsed ? "userInfo-collapsed" : ""}`}
              >
                <img src={perfil} alt="Perfil" width="50px" />
                {!collapsed && (
                  <div
                    style={{
                      width: "100%",
                      padding: "7px",
                      textAlign: "center",
                    }}
                  >
                    <div className="usernameSidebar">
                      <span style={{ fontSize: "12px" }}>
                        {userLogged.name} {userLogged.surname}
                      </span>
                    </div>
                    <Card
                      style={{
                        fontSize: "12px",
                        textAlign: "center",
                        margin: "2px auto",
                      }}
                    >
                      {newRoleConfig?.type ? "MODO DE PRUEBAS" : ""}
                    </Card>
                    <Card
                      style={{
                        fontSize: "12px",
                        textAlign: "center",
                        margin: "2px auto",
                      }}
                    >
                      {newRoleConfig?.type
                        ? newRoleConfig?.type
                        : userLogged?.full_rol_name}
                    </Card>
                  </div>
                )}
              </div>
            ) : (
              <div className="userSkeleton">
                <Skeleton variant="circular" width={50} height={50} />
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent
          className="sideBarContentClass"
          sx={{ position: "relative" }}
        >
          {visibleMenuConfig.map((section) => {
            if (section.type === "submenu") {
              return (
                <Menu key={section.id} iconShape="circle">
                  <SubMenu
                    className={
                      openSubMenu === section.id ? "active-submenu" : ""
                    }
                    title={intl.formatMessage({ id: section.label })}
                    icon={section.icon}
                    open={openSubMenu === section.id}
                    onClick={() =>
                      setOpenSubMenu((prev) =>
                        prev === section.id ? null : section.id,
                      )
                    }
                  >
                    {section.items.map((item) => (
                      <MenuItem key={item.id}>
                        {renderNavOrAnchor(item)}
                      </MenuItem>
                    ))}
                  </SubMenu>
                </Menu>
              );
            }

            return (
              <Menu key={section.id} iconShape="circle">
                {section.items.filter(isItemAllowed).map((item) => (
                  <MenuItem key={item.id} icon={item.icon}>
                    {renderNavOrAnchor(item)}
                  </MenuItem>
                ))}
              </Menu>
            );
          })}
          <div
            className="sidebar-btn-wrapper"
            style={{
              padding: "8px 8px 6px",
              marginTop: "auto",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {userData?.isSuperAdmin && collapsed ? (
              <AdminPanelSettings
                onClick={() => setOpenNewUserMode(true)}
                sx={{ cursor: "pointer" }}
              />
            ) : (
              userData?.isSuperAdmin && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    color="info"
                    size="small"
                    onClick={() => setOpenNewUserMode(true)}
                    startIcon={<AdminPanelSettings />}
                    sx={{
                      minHeight: "28px",
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      px: 0.75,
                      py: 0.5,
                      borderRadius: "10px",
                      lineHeight: 1.15,
                      textTransform: "uppercase",
                      boxShadow: "0 8px 16px rgba(59, 130, 246, 0.22)",
                      background:
                        "linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%)",
                      },
                    }}
                  >
                    Modo pruebas
                  </Button>

                  {newRoleConfig && (
                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      size="small"
                      onClick={exitTestMode}
                      disabled={!newRoleConfig}
                      sx={{
                        minHeight: "28px",
                        fontSize: "0.56rem",
                        fontWeight: 700,
                        px: 0.75,
                        py: 0.5,
                        borderRadius: "10px",
                        lineHeight: 1.15,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        boxShadow: "0 8px 16px rgba(239, 68, 68, 0.18)",
                      }}
                    >
                      Salir del modo prueba
                    </Button>
                  )}
                </Box>
              )
            )}
          </div>
        </SidebarContent>

        <SidebarFooter style={{ textAlign: "center" }}>
          <div
            className="sidebar-btn-wrapper"
            style={{
              padding: "10px 12px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {collapsed ? (
              <HelpRounded
                onClick={() => setOpenDialog(true)}
                sx={{ cursor: "pointer" }}
              />
            ) : (
              <Button
                sx={{
                  color: "#e2e8f0",
                  border: "1px solid rgba(148, 163, 184, 0.45)",
                  background:
                    "linear-gradient(180deg, rgba(15, 23, 42, 0.8), rgba(30, 41, 59, 0.92))",
                  borderRadius: "12px",
                  width: "100%",
                  minHeight: "36px",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                  "&:hover": {
                    borderColor: "rgba(96, 165, 250, 0.7)",
                    background:
                      "linear-gradient(180deg, rgba(30, 41, 59, 0.96), rgba(15, 23, 42, 1))",
                  },
                }}
                onClick={() => setOpenDialog(true)}
                startIcon={<HelpRounded />}
              >
                ATAJOS
              </Button>
            )}
          </div>

          {/* DIALOGS  */}
          <ChangeMode
            open={openNewUserMode}
            setOpen={setOpenNewUserMode}
            onClose={() => setOpenNewUserMode(false)}
            currentRoleId={userData?.user}
          />

          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Atajos de ayuda
              <IconButton
                aria-label="close"
                onClick={() => setOpenDialog(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {shortcuts.map((d) => (
                <div
                  key={d.id}
                  style={{ textAlign: "center", padding: "6px 0" }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      navigate(d.path);
                      setOpenDialog(false);
                    }}
                    sx={{
                      borderRadius: "12px",
                      borderColor: "rgba(59, 130, 246, 0.35)",
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0.02), rgba(59,130,246,0.04))",
                      color: "#0f172a",
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "rgba(59, 130, 246, 0.7)",
                        background:
                          "linear-gradient(180deg, rgba(59,130,246,0.08), rgba(59,130,246,0.12))",
                      },
                    }}
                  >
                    {d.label}
                  </Button>
                </div>
              ))}
            </DialogContent>
          </Dialog>
        </SidebarFooter>
      </ProSidebar>
      {/* Desktop chevron (visible on md+) */}
      {/* <DesktopToggle /> */}

      {/* Mobile hamburger (visible on < md) */}
      {/* <MobileToggle /> */}
    </div>
  );
}

export default Aside;
